import React from 'react';
import { find, get } from 'lodash';

import { Box, Divider, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { FORM, POINT_TYPE, SHIPMENT, STRINGS } from '../../../constants';
import { FormatterHelpers, ShipmentHelpers } from '../../../helpers';
import ParcelsPriceDetails from './ParcelsPriceDetails';

const CheckoutListRow = ({ label, price }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
    <Typography variant='body2'>{label}</Typography>
    <Typography variant='body2'>{price}</Typography>
  </Box>
);

const CollectDeliveryDetails = ({ shipment, price }) => {
  const collectFrom = get(shipment, FORM.SHIPMENT_FIELDS.COLLECT_FROM.KEY);
  const networkCode = get(shipment, FORM.SHIPMENT_FIELDS.NETWORK_CODE.KEY);
  const networkDesc = get(shipment, FORM.SHIPMENT_FIELDS.NETWORK_DESC.KEY);

  const selectedArrivalServiceExtraPrice =
    find(price.arrivalServicesExtraPrices, ['networkKey', networkCode]) || {};
  const getDisplayedExtraPrice = price =>
    price ? FormatterHelpers.formatPrice(price) : STRINGS.FREE;

  const getNetworkTitle = get(
    SHIPMENT.NETWORK_DETAILS,
    `[${networkCode}].title`
  );
  const label = getNetworkTitle
    ? getNetworkTitle(
        shipment?.outboundConsignment?._network?.service?.serviceDesc
      )
    : networkDesc;

  return (
    <>
      {collectFrom === POINT_TYPE.DOOR && (
        <CheckoutListRow
          label={STRINGS.SUMMARY_DETAILS.COLLECT_PARCEL}
          price={getDisplayedExtraPrice(price?.collectionExtraPrice)}
        />
      )}
      {collectFrom === POINT_TYPE.SHOP && (
        <CheckoutListRow
          label={STRINGS.DROP_OFF_TO_PICKUP}
          price={STRINGS.FREE}
        />
      )}
      {networkCode && (
        <CheckoutListRow
          label={label}
          price={getDisplayedExtraPrice(
            selectedArrivalServiceExtraPrice.extraPrice
          )}
        />
      )}
    </>
  );
};

const CheckoutListItem = ({
  basketItem,
  isLastItem,
  showMoreDetails,
  displayInsurance,
}) => {
  const { liabilityAmount, basePrice, totalIncVatAmount } =
    basketItem?.price || {};
  const receiverContactName = ShipmentHelpers.getReceiverContactName(
    basketItem?.shipment
  );
  const { parcels, _network } = basketItem?.shipment?.outboundConsignment || {};

  return (
    <React.Fragment key={basketItem.basketItemId}>
      <Box sx={{ py: 1 }}>
        <ParcelsPriceDetails
          receiverContactName={receiverContactName}
          parcels={parcels}
          networkDesc={_network?.networkDesc}
          showMoreDetails={showMoreDetails}
          price={
            FormatterHelpers.formatPrice(basePrice || totalIncVatAmount) ||
            STRINGS.FREE
          }
        />

        {showMoreDetails && (
          <CollectDeliveryDetails
            shipment={basketItem?.shipment}
            price={basketItem.price}
          />
        )}

        {displayInsurance && (
          <CheckoutListRow
            label={STRINGS.SUMMARY_DETAILS.INSURANCE_COVER}
            price={
              liabilityAmount
                ? FormatterHelpers.formatPrice(liabilityAmount)
                : STRINGS.FREE
            }
          />
        )}
      </Box>

      <Divider sx={{ display: isLastItem ? 'none' : 'block' }} />
    </React.Fragment>
  );
};

CheckoutListItem.propTypes = {
  basketItem: PropTypes.object,
  isLastItem: PropTypes.bool,
  showMoreDetails: PropTypes.bool,
  displayInsurance: PropTypes.bool,
};

const CheckoutPriceList = ({
  items = [],
  displayInsurance,
  showMoreDetails,
}) => (
  <>
    {items.map((basketItem, index) => {
      const insurance = get(
        basketItem,
        'shipment.outboundConsignment._network.insurance',
        false
      );

      return (
        <CheckoutListItem
          key={basketItem.basketItemId}
          basketItem={basketItem}
          isLastItem={items.length === index + 1}
          showMoreDetails={showMoreDetails}
          displayInsurance={displayInsurance && insurance}
        />
      );
    })}
  </>
);

CheckoutPriceList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  displayInsurance: PropTypes.bool,
  showMoreDetails: PropTypes.bool,
};

export default CheckoutPriceList;
